<template>
  <div class="container-custom moo-sheet-page moo-bundle-page" :style="styleObject">
    <div class="content-custom">
      <div
        class="moo-sheet-shell"
        :class="{ 'moo-sheet-shell--with-submit-bar': canRedeem && !isMyBundle }"
      >
        <MooSheetHead
          icon="inventory_2"
          :title="$t('rewardCatalogue.bundleTitle')"
          @close="goBack()"
        />

        <div v-if="isShowLayout">
          <div class="bd-hero">
            <div class="bd-hero__icon">
              <img v-if="bundle.icon_url" :src="bundle.icon_url" :alt="bundle.title" />
              <span class="material-icons-outlined" v-else>inventory_2</span>
            </div>
            <div class="bd-hero__body">
              <div class="bd-hero__name">{{ bundle.title || "" }}</div>
              <div class="bd-hero__meta" v-if="bundle.number_of_vouchers_text">
                {{ bundle.number_of_vouchers_text }}
              </div>
              <div class="bd-hero__meta" v-if="bundle.expiry_date_text">
                {{ $t('rewardCatalogue.validFor', { date: bundle.expiry_date_text }) }}
              </div>
              <div class="bd-hero__cost" v-if="!isMyBundle">{{ costText }}</div>
              <div class="bd-hero__cost" v-else>
                {{ $t('myVouchers.balance', { count: unusedVouchers.length }) }}
              </div>
            </div>
          </div>

          <!-- Owned bundle: the member's own vouchers from this purchase,
               split by whether they can still be used. -->
          <template v-if="isMyBundle">
            <div class="bd-tabs">
              <button
                v-for="tab in tabs"
                :key="tab.type"
                type="button"
                class="bd-tab"
                :class="{ 'bd-tab--active': typeSelected === tab.type }"
                @click="typeSelected = tab.type"
              >
                <span>{{ tab.label }}</span>
              </button>
            </div>

            <div class="bd-list" v-if="myVouchers.length">
              <div
                class="bd-mine"
                :class="{ 'bd-mine--spent': isSpentTab && !item.locked }"
                v-for="(item, index) in myVouchers"
                :key="index"
                @click="openMyVoucher(item)"
              >
                <div class="bd-mine__icon">
                  <img v-if="item.icon_url" :src="item.icon_url" :alt="item.reward_title" />
                  <span class="material-icons-outlined" v-else>redeem</span>
                </div>
                <div class="bd-mine__body">
                  <!-- Status on the name's line, same as MyVouchers' rows. -->
                  <div class="bd-mine__top">
                    <div class="bd-mine__name">{{ item.reward_title || "" }}</div>
                    <span
                      class="sheet-status"
                      :class="statusOf(item).modifier"
                      v-if="statusOf(item).label"
                    >
                      {{ statusOf(item).label }}
                    </span>
                  </div>
                  <div class="bd-mine__meta" v-if="item.expiry_date_text">
                    {{ $t('myVouchers.validUntil', { date: item.expiry_date_text }) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="bd-empty" v-else>
              <span class="material-icons-outlined">confirmation_number</span>
              <span>{{ $t('myVouchers.empty') }}</span>
            </div>
          </template>

          <template v-if="includedVouchers.length && !isMyBundle">
            <div class="bd-section-head">
              <div class="bd-section-title">
                {{ $t('rewardCatalogue.includedVouchers') }}
              </div>
              <span class="bd-total" v-if="bundle.number_of_vouchers_text">
                {{ bundle.number_of_vouchers_text }}
              </span>
            </div>
            <div class="bd-card bd-card--vouchers">
              <div
                class="bd-voucher"
                v-for="(voucher, index) in includedVouchers"
                :key="index"
              >
                <span class="material-icons-round bd-voucher__icon">confirmation_number</span>
                <span class="bd-voucher__name">{{ voucher.title }}</span>
                <span class="bd-voucher__qty">&times;{{ voucher.total }}</span>
              </div>
            </div>
          </template>

          <template v-if="termsLines.length">
            <div class="bd-section-title">{{ $t('rewardCatalogue.termsTitle') }}</div>
            <div class="bd-card">
              <div class="bd-row" v-for="(line, index) in termsLines" :key="index">
                <span class="material-icons-round bd-row__check">check_circle</span>
                <span>{{ line }}</span>
              </div>
            </div>
          </template>

          <SubmitOrderBar
            v-if="canRedeem && !isMyBundle"
            :label="$t('rewardCatalogue.redeem')"
            :loading="loaderActive"
            @submit="openRedeemConfirm()"
          />
        </div>
      </div>
    </div>

    <ModalConfirmSheet
      :message="confirmMessage"
      icon="inventory_2"
      :confirm-text="$t('common.yes')"
      :cancel-text="$t('common.cancel')"
      :process-yes="processExchange"
    >
      <div class="sheet-methods" v-if="showPayPicker">
        <button
          type="button"
          class="sheet-method"
          :class="{
            'sheet-method--selected': pickedPay === PAY_BALANCE,
            'sheet-method--disabled': bundle.is_disabled,
          }"
          :disabled="bundle.is_disabled"
          @click="pickedPay = PAY_BALANCE"
        >
          <span class="sheet-method__radio"></span>
          <span class="material-icons-round sheet-method__icon">{{
            isPointMerchant ? "stars" : "account_balance_wallet"
          }}</span>
          <span class="sheet-method__body">
            <span class="sheet-method__label">{{ balanceMethodLabel }}</span>
            <span class="sheet-method__note">
              {{ balanceText }}
              <template v-if="bundle.is_disabled">
                — {{ $t('rewardCatalogue.notEnough') }}
              </template>
            </span>
          </span>
        </button>

        <button
          type="button"
          class="sheet-method"
          :class="{
            'sheet-method--selected': pickedPay === PAY_CARD,
            'sheet-method--disabled': bundle.is_disabled_by_card,
          }"
          :disabled="bundle.is_disabled_by_card"
          @click="pickedPay = PAY_CARD"
        >
          <span class="sheet-method__radio"></span>
          <span class="material-icons-round sheet-method__icon">credit_card</span>
          <span class="sheet-method__body">
            <span class="sheet-method__label">{{ $t('rewardCatalogue.payCard') }}</span>
            <span class="sheet-method__note">{{ $t('rewardCatalogue.payCardNote') }}</span>
          </span>
        </button>
      </div>
    </ModalConfirmSheet>

    <ModalSuccess
      :processAcction="gotoMyVouchers"
      :success="true"
      :message="successMessage"
      :buttonText="$t('rewardCatalogue.goToMyVouchers')"
    />

    <VoucherCodeSheet
      :detail="voucherDetail"
      :show-qr="!isSpentTab"
      @copied="onCodeCopied"
    />

    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import MooSheetHead from "@/components/MooSheetHead.vue";
import ModalConfirmSheet from "@/components/ModalConfirmSheet.vue";
import ModalSuccess from "@/components/ModalSuccess.vue";
import SubmitOrderBar from "@/components/SubmitOrderBar.vue";
import PromocodeService from "../services/promocode.service";
import MemberService from "../services/member.service";
import { STRIPE, AIRWALLEX } from "@/config/const";
import { stashPendingRedeem } from "@/config/utils";
import VoucherCodeSheet from "@/components/VoucherCodeSheet.vue";
import $ from "jquery";

// merchant_setting.reward_per_transaction value meaning "points, not cashback".
const REWARD_POINT = 2;

// Which half of an owned bundle is on screen.
const TYPE_ACTIVE = 1;
const TYPE_SPENT = 2;

const PAY_BALANCE = "balance";
const PAY_CARD = "card";

export default {
  name: "VoucherBundleDetail",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    MooSheetHead,
    ModalConfirmSheet,
    ModalSuccess,
    VoucherCodeSheet,
    SubmitOrderBar,
  },
  data() {
    return {
      bundle: {},
      merchantMember: {},
      currentMember: {},
      isShowLayout: false,
      PAY_BALANCE,
      PAY_CARD,
      pickedPay: PAY_BALANCE,
      paymentMode: STRIPE,
      loaderActive: false,
      snackbar: null,
      message: "",
      // Reward mode: opened from MyVouchers for a bundle the member already
      // bought, rather than from the catalogue for one on sale. The two share
      // one endpoint and one screen because the header is identical — only what
      // sits under it differs.
      isMyBundle: !!this.$route.query.is_reward,
      memberVoucherBundleId: this.$route.query.member_voucher_bundle_id ?? 0,
      unusedVouchers: [],
      usedVouchers: [],
      typeSelected: TYPE_ACTIVE,
      voucherDetail: {},
      TYPE_ACTIVE,
      TYPE_SPENT,
    };
  },
  created() {
    if (!this.isMyBundle) {
      this.getDefaultPaymentMode();
    }
    this.getBundleDetail();
  },
  computed: {
    styleObject() {
      return {
        "--color":
          this.$store.state.mainThemeColorTheme2 ||
          this.$store.state.color ||
          "#201a1a",
      };
    },
    isPointMerchant() {
      return this.merchantMember.reward_type == REWARD_POINT;
    },
    tabs() {
      return [
        { type: TYPE_ACTIVE, label: this.$t("myVouchers.tabNew") },
        { type: TYPE_SPENT, label: this.$t("myVouchers.tabUsed") },
      ];
    },
    isSpentTab() {
      return this.typeSelected === TYPE_SPENT;
    },
    // The API splits the bundle on `use` and expiry only, so a locked voucher
    // arrives in the unused list. It cannot be redeemed today either, so it
    // belongs with the rest of what the member cannot use — re-partitioned here
    // rather than in the query, because the member site shows locked vouchers in
    // its own New tab and that behaviour is not ours to change.
    availableVouchers() {
      return this.unusedVouchers.filter((item) => !item.locked);
    },
    spentVouchers() {
      // Locked first: they are still live and may unlock, unlike the used and
      // expired ones behind them, which are history.
      return [
        ...this.unusedVouchers.filter((item) => item.locked),
        ...this.usedVouchers,
      ];
    },
    myVouchers() {
      return this.isSpentTab ? this.spentVouchers : this.availableVouchers;
    },
    // A bundle priced in points only shows a point cost when it actually has
    // one — the member site makes the same distinction.
    costText() {
      return this.isPointMerchant && this.bundle.points_convert > 0
        ? this.bundle.point_text
        : this.bundle.amount_text;
    },
    balanceText() {
      return this.isPointMerchant
        ? this.merchantMember.point_format
        : this.merchantMember.amount_format;
    },
    balanceMethodLabel() {
      return this.isPointMerchant
        ? this.$t("rewardCatalogue.payPoint", { name: this.pointName })
        : this.$t("rewardCatalogue.payWallet");
    },
    pointName() {
      return this.merchantMember.point_name || "Point";
    },
    // The BE indexes detail by voucher_id, so PHP serialises it as an OBJECT
    // keyed by id, not an array — reading .length on it gives undefined and the
    // whole section silently disappears.
    includedVouchers() {
      const detail = this.bundle.number_of_vouchers?.detail;
      if (!detail) {
        return [];
      }
      return Array.isArray(detail) ? detail : Object.values(detail);
    },
    termsLines() {
      return (this.bundle.terms_arr ?? []).filter((line) => line);
    },
    // Open while at least one method is usable: a bundle out of reach on
    // balance can still be bought by card.
    canRedeem() {
      if (!this.isShowLayout) {
        return false;
      }
      return !this.bundle.is_disabled || !this.bundle.is_disabled_by_card;
    },
    showPayPicker() {
      return !this.bundle.is_disabled_by_card;
    },
    confirmMessage() {
      if (!this.bundle || !this.bundle.id) {
        return "";
      }
      const rows = [
        {
          icon: "inventory_2",
          label: this.$t("rewardCatalogue.rowType"),
          value: this.bundle.number_of_vouchers_text ?? "",
        },
        {
          icon: this.isPointMerchant ? "stars" : "payments",
          label: this.$t("rewardCatalogue.rowCost"),
          value: this.costText,
        },
        {
          icon: "account_balance_wallet",
          label: this.$t("rewardCatalogue.rowBalance"),
          value: this.balanceText,
        },
      ];

      const rowsHtml = rows
        .map(
          (r) => `
        <div class="ps-row">
          <span class="material-icons-outlined ps-row-icon">${r.icon}</span>
          <span class="ps-row-label">${r.label}</span>
          <span class="ps-row-value">${r.value ?? ""}</span>
        </div>`
        )
        .join("");

      return `
        <span class="ps-title">${this.$t("rewardCatalogue.confirmTitle")}</span>
        <span class="ps-amount">${this.bundle.title || ""}</span>
        <span class="ps-rows">${rowsHtml}</span>
      `;
    },
    // Shared with RewardCatalogue's sheet, which renders the same two lines
    // through the same ModalSuccess component.
    successMessage() {
      return `
        <span class="ps-title">${this.$t("rewardCatalogue.redeemSuccess")}</span>
        <span class="ps-amount">${this.bundle.title || ""}</span>
      `;
    },
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    // The bundles and vouchers the member now HOLDS, not MyReward — that screen
    // picks a voucher to apply to the order in hand. Same destination the
    // member site sends them to after a redeem.
    gotoMyVouchers() {
      $("#success-modal .btn-close").click();
      this.$router.push({ name: "MyVouchers", params: this.$route.params });
    },
    openRedeemConfirm() {
      this.pickedPay = this.bundle.is_disabled ? PAY_CARD : PAY_BALANCE;
      $(".btn-confirm-sheet-modal").click();
    },
    getDefaultPaymentMode() {
      MemberService.getDefaultPaymentMode().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.paymentMode =
              parseInt(response.data.data.default_payment_mode) || STRIPE;
          }
        },
        () => {}
      );
    },
    getBundleDetail() {
      this.showLoader();
      PromocodeService.getVoucherBundleDetail({
        id: this.$route.params.id,
        is_reward: this.isMyBundle ? 1 : 0,
        // Picks which purchase to open when the member bought the same bundle
        // more than once; ignored by the store view.
        member_voucher_bundle_id: this.memberVoucherBundleId,
      }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.bundle = response.data.data.voucher ?? {};
            this.merchantMember = response.data.data.merchant_member ?? {};
            this.currentMember = response.data.data.member ?? {};
            this.unusedVouchers = response.data.data.voucher_in_bundle_unused ?? [];
            this.usedVouchers = response.data.data.voucher_in_bundle_used ?? [];
            this.isShowLayout = true;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? "Cannot load bundle";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    processExchange() {
      // Both sheet buttons carry data-bs-dismiss; it is already closing.
      if (!this.bundle.id) {
        return;
      }
      if (this.pickedPay === PAY_CARD) {
        this.payByCard();
        return;
      }
      this.closeSnackbar();
      this.showLoader();

      PromocodeService.exchangeVoucherBundle({ reward_id: this.bundle.id }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.getBundleDetail();
            $(".btn-success-modal").click();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error;
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    // The bundle is issued server-side by the webhook off
    // metadata.isExchangeVoucherBundle. Sending the member back to the
    // catalogue, which already knows how to verify a gateway return.
    payByCard() {
      this.closeSnackbar();
      this.showLoader();

      const merchantCode = this.$store.state.merchantCode;
      let url = location.protocol + "//" + location.host + "/" + merchantCode;
      if (this.$route.params.table_code) {
        url +=
          "/QR/" +
          this.$store.state.branchCode +
          "/" +
          this.$route.params.table_code +
          "/reward-catalogue";
      } else {
        url += "/" + this.$store.state.branchCode + "/reward-catalogue";
      }

      // This screen hands the member to the catalogue on the way back, so the
      // sheet that reports the result is the catalogue's. Leave it what it needs.
      stashPendingRedeem({
        title: this.bundle.title || "",
        kindLabel: this.$t("rewardCatalogue.bundleVoucher"),
        costText: this.costText,
        costIcon: this.isPointMerchant ? "stars" : "payments",
      });

      const dataSend = {
        images: "",
        name: "VOUCHER BUNDLE " + (this.bundle.title || ""),
        unit_amount: this.bundle.amount_convert,
        quantity: 1,
        member_id: this.currentMember.id,
        isExchangeVoucherBundle: 1,
        voucher_id: this.bundle.id,
        // Marked so the landing page can tell "came back from the gateway"
        // from a normal visit — cancelling leaves the checkout page sitting in
        // history behind us exactly the way paying does.
        cancel_url: url + "?exchange=cancel",
      };

      const isAirwallex = this.paymentMode == AIRWALLEX;
      const request = isAirwallex
        ? MemberService.checkoutAirwallex({
            ...dataSend,
            success_url: url + "?exchange=success",
          })
        : MemberService.checkoutStripe({
            ...dataSend,
            success_url:
              url + "?session_id={CHECKOUT_SESSION_ID}&exchange=success",
          });

      request.then(
        (response) => {
          if (response.data.result.isSuccess) {
            window.location.href = response.data.data.url;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
            this.hideLoader();
          }
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? "Checkout failed";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    // Same chips MyVouchers puts on its rows, read from the same fields.
    statusOf(item) {
      if (item.locked) {
        return {
          label: this.$t("myVouchers.statusLocked"),
          modifier: "sheet-status--locked",
        };
      }
      if (!this.isSpentTab) {
        return { label: "", modifier: "" };
      }
      return item.use
        ? { label: this.$t("myVouchers.statusUsed"), modifier: "sheet-status--used" }
        : {
            label: this.$t("myVouchers.statusExpired"),
            modifier: "sheet-status--expired",
          };
    },
    // member_reward_id, not voucher_id: inside a bundle the row already IS the
    // member's own copy, and that id is what resolves its code.
    openMyVoucher(item) {
      this.showLoader();
      PromocodeService.getRewardDetail(item.member_reward_id, 1).then(
        (response) => {
          if (response.data.result.isSuccess) {
            const reward = response.data.data.reward ?? {};
            if (item.expiry_date_text) {
              reward.expiry_date_text = item.expiry_date_text;
            }
            this.voucherDetail = reward;
            $(".btn-voucher-code-modal").click();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? "Cannot load voucher";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },
    onCodeCopied() {
      this.snackbar = "success";
      this.message = this.$t("myVouchers.copied");
      this.setTimeOutNotification();
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
};
</script>

<style scoped>
/* Shell, tokens, sheet head and modal chrome come from moo-sheet.css. */
.container-custom {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.content-custom {
  flex: 1;
  overflow: auto;
}

.bd-hero {
  display: flex;
  gap: 14px;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 16px;
}
.bd-hero__icon {
  width: 76px;
  height: 76px;
  flex-shrink: 0;
  border-radius: 14px;
  background: var(--sheet-08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.bd-hero__icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.bd-hero__icon .material-icons-outlined {
  font-size: 30px;
  color: var(--sheet);
}
.bd-hero__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.bd-hero__name {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #241a12;
  word-break: break-word;
}
.bd-hero__meta {
  font-size: 12px;
  color: #857372;
  margin-top: 3px;
}
.bd-hero__cost {
  font-size: 18px;
  font-weight: 800;
  color: var(--sheet);
  margin-top: 8px;
}

.bd-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #241a12;
  margin-bottom: 10px;
}
.bd-section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.bd-total {
  font-size: 12px;
  color: #857372;
  white-space: nowrap;
}

/* Quantity is its own column rather than an "x2" glued to the title, and it is
   shown even when it is 1 — otherwise the rows give no clue how they add up to
   the bundle total. */
.bd-card--vouchers {
  gap: 0;
  padding: 0;
}
.bd-voucher {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 14px;
  font-size: 13px;
  line-height: 1.45;
  color: #6b5d55;
}
.bd-voucher + .bd-voucher {
  border-top: 1px solid var(--sheet-border);
}
/* Own rule rather than sharing .bd-row__check: that one carries margin-top for
   a flex-start row, and Material Icons only render crisply on their own even
   grid (16/18/20/24) — 17px straddles half-pixels and reads as misaligned. */
.bd-voucher__icon {
  font-size: 16px;
  color: var(--sheet);
  flex-shrink: 0;
}
.bd-voucher__name {
  flex: 1;
  min-width: 0;
  word-break: break-word;
  color: #241a12;
  font-weight: 600;
  padding-top: 4px;
}
.bd-voucher__qty {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--sheet);
  background: var(--sheet-12);
  border-radius: 9999px;
  padding: 2px 9px;
}
.bd-card {
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.bd-row {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 13px;
  line-height: 1.5;
  color: #6b5d55;
}
.bd-row__check {
  font-size: 18px;
  color: var(--sheet);
  flex-shrink: 0;
  margin-top: 1px;
}

/* ---- owned bundle: the member's own vouchers ---- */
.bd-tabs {
  display: flex;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 12px;
  padding: 3px;
  margin-bottom: 12px;
}
.bd-tab {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  border-radius: 9px;
  padding: 8px 4px;
  font-size: 13px;
  font-weight: 600;
  color: #857372;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
/* The label needs its own box: text-overflow does not apply to the anonymous
   text of a flex item, so a long translation would be cut mid-word instead. */
.bd-tab span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bd-tab--active {
  background: var(--sheet);
  color: #fff;
}
.bd-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}
.bd-mine {
  display: flex;
  gap: 12px;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 16px;
  padding: 12px;
  cursor: pointer;
  transition: transform 0.1s ease;
}
.bd-mine:active {
  transform: scale(0.995);
}
/* Tinted card + chip carry "spent"; the text keeps full contrast. A locked
   voucher shares this tab but not this tint — it is not history, it is waiting. */
.bd-mine--spent {
  background: var(--sheet-bg);
}
.bd-mine__icon {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 12px;
  background: var(--sheet-08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.bd-mine__icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.bd-mine__icon .material-icons-outlined {
  font-size: 24px;
  color: var(--sheet);
}
.bd-mine__body {
  flex: 1;
  min-width: 0;
}
.bd-mine__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.bd-mine__name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: #241a12;
  overflow-wrap: anywhere;
}
.bd-mine__meta {
  margin-top: 2px;
  font-size: 12px;
  color: #857372;
}
.bd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 16px;
  color: #857372;
  font-size: 13px;
  text-align: center;
}
.bd-empty .material-icons-outlined {
  font-size: 32px;
  color: var(--sheet-border);
}
</style>
